import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getCategoryName } from '~/utils/categoryHelper';

interface Category {
  id: string;
  [key: string]: unknown;
}

interface CategoryNavBarProps {
  /** Array of categories to display */
  categories: Category[];
  /** Currently active category ID (null = "All") */
  activeCategory: string | null;
  /** Whether the navbar is sticky */
  isSticky: boolean;
  /** Theme for styling */
  theme: {
    backgroundColor?: string;
    borderColor?: string;
    primaryColor?: string;
    borderRadius?: string;
  } | null;
  /** Ref for the scroll container */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Handler for mouse down (drag start) */
  onMouseDown: (e: React.MouseEvent) => void;
  /** Handler for mouse move (dragging) */
  onMouseMove: (e: React.MouseEvent) => void;
  /** Handler for mouse up/leave (drag end) */
  onMouseUp: () => void;
  /** Whether currently dragging */
  isDragging: React.RefObject<boolean>;
  /** Callback when "All" is clicked */
  onScrollToAll: () => void;
  /** Callback when a category is clicked */
  onScrollToCategory: (categoryId: string) => void;
}

/**
 * Sticky category navigation bar with drag-to-scroll support.
 * Displays category buttons that scroll the page to the corresponding section.
 */
export function CategoryNavBar({
  categories,
  activeCategory,
  isSticky,
  theme,
  containerRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  isDragging,
  onScrollToAll,
  onScrollToCategory,
}: CategoryNavBarProps) {
  const { t, i18n } = useTranslation();

  return (
    <div
      className={`z-25 border-b w-full transition-all duration-300 ${
        isSticky
          ? 'fixed top-0 left-0 right-0 backdrop-blur-md shadow-sm'
          : 'relative backdrop-blur-sm'
      }`}
      style={{
        backgroundColor: isSticky
          ? `${theme?.backgroundColor}F0`
          : `${theme?.backgroundColor}CC`,
        borderColor: `${theme?.borderColor}20`,
      }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="py-1 ">
          <div
            ref={containerRef}
            className="flex gap-2 overflow-x-auto cursor-grab active:cursor-grabbing select-none scrollbar-hide py-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {/* All Categories Button */}
            <motion.button
              onClick={() => {
                if (isDragging.current) return;
                onScrollToAll();
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors"
              style={{
                backgroundColor: !activeCategory
                  ? theme?.primaryColor
                  : `${theme?.primaryColor}15`,
                color: !activeCategory ? '#fff' : theme?.primaryColor,
                borderRadius: theme?.borderRadius,
                border: !activeCategory
                  ? 'none'
                  : `1px solid ${theme?.primaryColor}30`,
              }}
            >
              {t('menu.allCategories')}
            </motion.button>

            {/* Category Buttons */}
            {categories.map((category) => (
              <motion.button
                key={category.id}
                data-category-id={category.id}
                onClick={() => {
                  if (isDragging.current) return;
                  onScrollToCategory(category.id);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap"
                style={{
                  backgroundColor:
                    activeCategory === category.id
                      ? theme?.primaryColor
                      : `${theme?.primaryColor}15`,
                  color:
                    activeCategory === category.id
                      ? '#fff'
                      : theme?.primaryColor,
                  borderRadius: theme?.borderRadius,
                  border: `1px solid ${theme?.primaryColor}30`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {getCategoryName(category as any, i18n.language)}
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CategoryNavBar;
